<script setup>
import { ref, computed } from 'vue';
import { modal } from '@shop/frontend/web/js/components/Modal';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import ITeams from '@components/ITeams/ITeams.vue';
import ITicket from '@components/ITicket/ITicket.vue';
import IRefund from '@components/IRefund/IRefund.vue';
import IPackageEvent from '@components/IPackageEvent/IPackageEvent.vue';
import IPackageEventName from '@components/IPackageEventName/IPackageEventName.vue';
import ILink from '@components/ILink/ILink.vue';
import ExtraInfo from '@components/ExtraInfo.vue';
import useCartStore from '@vueApp/store/modules/cart';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

dayjs.locale('ru');

const cartStore = useCartStore();
const props = defineProps({
  itemCase: {
    type: Object,
    required: true,
  },
  trashButton: {
    type: Boolean,
    default: true,
  },
  isHistory: {
    type: Boolean,
    default: false,
  },
  paidAtTimestamp: {
    type: String,
    default: '',
  },
  orderId: {
    type: [Number, String],
    default: '',
  },
});

const emits = defineEmits(['removeEvent']);

function removeTicket(ticket, eventId, type) {
  const adultTicketsCount = props.itemCase.tickets.reduce((
    ticketsCount,
    currentTicket,
  ) => (currentTicket.loyaltyCategoryId === null ? ticketsCount + 1 : ticketsCount), 0);

  const hasChildTicket = props.itemCase.tickets.some(
    (currentTicket) => currentTicket.loyaltyCategoryId === 1,
  );

  if (hasChildTicket && adultTicketsCount <= 1) {
    modal.error(translator.t('order', 'Need at least one adult ticket!'));
    return;
  }

  if (type === 'service_simple') {
    cartStore.removeServiceTicket(ticket, eventId);
  } else {
    cartStore.removeEventTicket(ticket, eventId);
  }
}

function removeEvent(eventId, type, countTickets) {
  if (type === 'service_simple') {
    cartStore.removeService(eventId, props.itemCase.orderId, countTickets);
  } else {
    cartStore.removeEvent(eventId, props.itemCase.orderId);
  }
}

function changeLoyalty(ticket, eventId, categoryId, priceId) {
  cartStore.changeLoyalty(ticket, eventId, props.itemCase.orderId, categoryId, priceId);
}

const isPackageEventsShow = ref(false);

const wasRefund = computed(() => props.itemCase.tickets.some((ticket) => ticket.isRefunded));

const isMobile = computed(() => window.innerWidth <= 991);
</script>

<template>
  <div class="i-order-event-item">
    <div class="i-order-event-item__top-wrapper">
      <a class="i-order-event-item__team-link" :href="itemCase.pageLink">
        <div class="i-order-event-item__top-left">
          <template v-if="itemCase.type == 'vagrant'">
            <div class="i-order-event-item__name">
              <IPackageEventName
                v-if="itemCase.eventType === 'Пакет' && itemCase.participants?.team"
                :package-event="itemCase"
              />
              <ITeams
                v-else-if="itemCase.participants?.team"
                :first-team="itemCase.participants.team.homeTeam"
                :second-team="itemCase.participants.team.guestTeam"
                :is-home-logo-with-opponent="itemCase.isHomeLogoWithOpponent"
                :name="itemCase.name"
              />
              <div v-else class="name">
                <div class="name__img">
                  <img
                    :src="itemCase.participants?.singular.imageUrl"
                    alt="eventImage"
                  >
                </div>
                <div class="name__title">
                  {{ itemCase.participants?.singular.name }}
                </div>
              </div>
            </div>
            <div v-if="itemCase.eventType !== 'Пакет'" class="i-order-event-item__details">
              <span class="time">
                <svg>
                  <use xlink:href="#calendar" />
                </svg>
                <span class="text-capitalize">
                  {{ dayjs(itemCase.startTimestampStr).format("DD.MM.YYYY HH:mm") }}
                </span>
              </span>
              <span class="place">
                <svg>
                  <use xlink:href="#location" />
                </svg>
                {{ itemCase.unitName }}
              </span>
            </div>
          </template>
          <template v-else>
            <div
              class="i-order-event-item__name"
              :class="{'i-order-event-item__name_service' : itemCase.type === 'service_simple'}"
            >
              <div class="name">
                <div class="name__title">{{ itemCase.name }}</div>
              </div>
            </div>
          </template>
        </div>
      </a>
      <button
        v-if="itemCase.eventType === 'Пакет'"
        class="i-order-event-item__show-packages-btn"
        :class="{ 'i-order-event-item__show-packages-btn_active' : isPackageEventsShow }"
        @click="isPackageEventsShow = !isPackageEventsShow"
      >
        <svg height="24" width="24">
          <use xlink:href="#chevron-down" />
        </svg>
      </button>
    </div>
    <div class="i-order-event-item__top-right">
      <div v-if="!isHistory && isMobile" class="i-order-event-item__summary-price mobile">
        Цена: <span>{{ itemCase.summaryPrice }} ₽</span>
      </div>
      <div v-else class="i-order-event-item__summary-price">
        Стоимость: <span>{{ itemCase.summaryPrice }} ₽</span>
      </div>
      <ILink
        v-if="trashButton"
        icon-name="trash"
        class="trash-btn"
        @click="
          removeEvent(
            itemCase.id,
            type ?? itemCase.type,
            ticketsCount ?? itemCase.tickets.length
          )
        "
      >
        Удалить
      </ILink>
      <div v-if="isHistory && wasRefund" class="i-order-event-item__was-refund js-was-refund">
        Билеты были возвращены
      </div>
    </div>
    <div
      v-if="itemCase?.packageEvents?.length"
      v-show="isPackageEventsShow"
      class="i-order-event-item__package-events"
    >
      <IPackageEvent
        v-for="packageEvent in itemCase.packageEvents"
        :key="packageEvent.startTimestamp" :package-event="packageEvent"
      />
    </div>
    <div class="i-order-event-item__tickets">
      <slot>
        <div v-for="ticket in itemCase.tickets" :key="ticket.place" class="tickets-item">
          <i-ticket
            :ticket="ticket"
            :is-history="isHistory"
            :inactive="false"
            :short="false"
            :allowed-loyalty="itemCase.allowedLoyaltyCategories"
            :order-id="itemCase.orderId"
            @remove-ticket="
              curTicket => removeTicket(curTicket, itemCase.id, itemCase.type)
            "
            @change-loyalty="
              ({ ticket, categoryId, priceId }) =>
                changeLoyalty(ticket, itemCase.id, categoryId, priceId)
            "
          />
        </div>
      </slot>
    </div>
    <div v-if="isHistory" class="history-item__low">
      <span v-if="paidAtTimestamp" class="history-item__low-name">
        {{ translator.t("main", "Date of purchase") }}:&nbsp;
        <strong>{{ paidAtTimestamp }}</strong>
      </span>
      <span class="history-item__low-number">
        {{ translator.t('main', 'Order') }} № {{ orderId }}
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: height 0.5s ease-in-out;
}

.fade-enter-to,
.fade-leave-from {
  overflow: hidden;
  height: 100%;
}

.fade-enter-from,
.fade-leave-to {
  overflow: hidden;
  height: 0;
}

.i-order-event-item {
  background-color: $white;
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;

  &__sub-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &+.i-order-event-item {
    margin-top: 20px;
  }

  &__top {
    display: flex;
    justify-content: space-between;
  }

  &__top-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    font-size: 14px;

    .trash-btn {
      color: $primary;
      padding: 5px;
      margin-top: 15px;
    }
  }

  &__name {
    .name {
      display: flex;
      align-items: center;
      margin-bottom: 20px;

      &__img {
        width: 50px;
        height: 50px;
        margin-right: 10px;

        img {
          width: 100%;
          height: 100%;
        }
      }

      &__title {
        font-weight: 700;
        font-size: 20px;
        color: $color;
        text-transform: uppercase;
      }
    }

    .i-teams {
      .i-teams__wrapper {
        color: red;

        .i-teams__item {
          max-width: auto;
        }
      }
    }
  }

  &__summary-price {
    margin-bottom: 20px;

    span {
      font-weight: 700;
      font-size: 20px;
    }
  }

  &__details {
    margin-bottom: 20px;
    display: flex;
    align-items: center;

    .time,
    .place {
      display: flex;
      align-items: center;
      font-size: 14px;

      svg {
        width: 15px;
        height: 15px;
        margin-right: 10px;
        margin-top: -1px;
        stroke: currentColor;
      }
    }

    .time {
      margin-right: 32px;
      color: $small-text;
      font-weight: 600;
    }

    .place {
      font-weight: 400;
    }
  }

  &__tickets {
    width: 100%;
    display: flex;
    flex-wrap: wrap;

    .tickets-item {
      margin-right: 20px;
      margin-bottom: 20px;
    }
  }

  &__top-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__package-events {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
    width: 90%;
  }

  &__top-wrapper {
    display: flex;
    align-items: baseline;
    margin-bottom: 20px;
  }

  &__show-packages-btn {
    margin-left: 20px;
    padding: 5px 5px 5px 5px;
    transition: transform .3s ease;
  }

  &__show-packages-btn_active {
    transform: rotate(180deg);
  }

  &__was-refund {
    padding: 5px 10px;
    border: 1px solid $danger-color;
    border-radius: 5px;
    color: $danger-color;
    font-size: 13px;
    pointer-events: none;
  }

  @media (max-width: $screen-md) {
    &__top {
      flex-direction: column;
    }

    &__top-left {
      order: 1;
    }

    &__top-right {
      order: 4;
      align-items: flex-start;
      margin-bottom: 0;
    }

    &__details {
      margin-bottom: 20px;

      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    &__tickets {
      order: 2;
    }

    .mobile {
      display: inline-block !important;
    }

    .close-btn {
      position: absolute;
      top: 8px;
      right: 10px;
    }

    &__summary-price {
      font-size: 14px;
      margin-bottom: 0;
      font-family: 'Roboto', Arial, sans-serif;

      span {
        font-weight: 700;
        font-size: 16px;
      }
    }

    &__package-events {
      width: 100%;
      gap: 16px;
    }

    &__was-refund {
      margin-top: 10px;
    }
  }

  @media (max-width: $screen-xs) {
    width: 100%;

    .i-teams {
      margin-bottom: 20px;
    }

    &__tickets {
      margin-bottom: 12px;

      .tickets-item {
        margin-right: 0px;
        width: 100%;
        margin-bottom: 16px;
      }
    }

    &__sub-wrapper {
      gap: 10px;
    }

    &__team-link {
      display: flex;
      width: 100%;
    }

    &__top-left {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &__name {
      align-self: center;
      width: 100%;

      &_service {
        align-self: flex-start;
      }
    }

    &__top-right {
      margin-bottom: 8px;
      line-height: 18px;

      .trash-btn {
        display: none;
      }
    }

    &__top-wrapper {
      width: 100%;
      margin-bottom: 0;
    }

    &+.i-order-event-item {
      padding-top: 16px;
      border-top: 1px solid $border-light-gray;
      margin-top: 16px;
    }
  }
}

.order {
  .i-order-event-item__summary-price {
    font-size: 16px;

    @media (max-width: $screen-xs) {
      font-size: 14px;
    }
  }
}

.history-item {
  .i-order-event-item__top-wrapper {
    margin-bottom: 0;
  }
}
</style>
